import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AnimationActivityContext } from 'contexts/AnimationActivityContext';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import { TableIdContext } from 'contexts/TableIdContext';
import { SeatLayoutSampler, SeatPosition } from 'hooks/useEllipticalPositions';
import { isTableResyncing } from 'utils/tableResyncState';

import { useLayoutSeatId } from 'components/SitGoWaiting/useSitGoWaiting';

type SeatRotationContextType = {
  /** True while the POV seat-assignment rotation is in flight (or queued). */
  isRotating: boolean;
  /**
   * The seat the ring is laid out around: the viewer's seat, or their drawn
   * seat on a Sit & Go still starting (EN-11257). Undefined outside a
   * provider, where callers fall back to the viewer's seat.
   */
  layoutSeatId?: number;
  /** Continuous slot index for a seat during the rotation (0 = bottom center). */
  slotFor: (seatId: number) => number;
  /** Sampler matching the live seat layout (arc positions for a slot). */
  seatAt: (slot: number) => SeatPosition | undefined;
  /** Sampler for bet-chip positions, matching `seatAt`. */
  betAt: (slot: number) => SeatPosition | undefined;
  /**
   * True while the ring is rotating, settled within the last
   * `ROTATION_SETTLE_GRACE_MS`, OR the POV changed within that same window —
   * see the render-time detection below for why that third case exists and
   * cannot be folded into "is rotating". A POV flip (e.g. the GUID-match
   * hiccup a join-triggered resync can cause, or a seat leaving and leaving
   * the remaining player alone — see the resync branch below) is also what
   * can remount `WinChips` for the seat that just became/stopped being POV:
   * its `getSeatPosition` lookup depends on `currentPlayerSeatId`, so a
   * stale/duplicate re-delivery of an already-paid award landing in that same
   * window remounts the SAME award and replays its flight right as — or just
   * after — the seats finish turning (EN-10725). Read inside an effect, never
   * as a dependency, same convention as `useTableSnap`'s `shouldSnap`.
   */
  shouldHoldForRotation: () => boolean;
};

const noopSampler = () => undefined;

export const SeatRotationContext = createContext<SeatRotationContextType>({
  isRotating: false,
  slotFor: () => 0,
  seatAt: noopSampler,
  betAt: noopSampler,
  shouldHoldForRotation: () => false
});

export const useSeatRotation = () => useContext(SeatRotationContext);

// Spectator (no POV) is laid out exactly like POV on seat 1 (seat N at slot
// N-1), so we treat "unseated" as seat 1 for rotation purposes.
const effectivePov = (seatId: number) => (seatId < 1 ? 1 : seatId);

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const mod = (value: number, m: number) => ((value % m) + m) % m;

// How long after a spin settles its seats are still considered "just moved"
// for `shouldHoldForRotation` — long enough to cover a trailing re-render or
// two from the same burst that caused the spin, short enough to never hold a
// genuinely new award back from animating.
const ROTATION_SETTLE_GRACE_MS = 600;

type ProviderProps = {
  seatCount: number;
  sampler: SeatLayoutSampler;
  children: React.ReactNode;
};

/**
 * Animates the seat ring when the POV seat changes: the newly assigned POV seat
 * spins clockwise along the ellipse until it reaches the bottom-center, with
 * every other seat carried along by the same rotation. Outside of that
 * transition the ring is static and consumers fall back to the discrete layout.
 *
 * The spin only *starts* once nothing else is animating — neither the dealer
 * button (`DealerAnimationContext.isAnimating`) nor a previous seat rotation
 * (serialized via `runningRef`). Until then the ring is held at its pre-rotation
 * layout (progress 0) instead of snapping, and back-to-back seat changes chain
 * one after another.
 */
export const SeatRotationProvider = ({ seatCount, sampler, children }: ProviderProps) => {
  // The viewer's seat, or their drawn seat on a Sit & Go still filling
  // (EN-11257): the same seat Seats lays the felt out around.
  const pov = useLayoutSeatId();
  // Which table this ring belongs to, so the resync window below is read per
  // table rather than client-wide (see utils/tableResyncState).
  const resyncGameID = useContext(TableIdContext);
  const { isAnimating: dealerAnimating } = useContext(DealerAnimationContext);
  const { isAnimating: chipsAnimating } = useContext(AnimationActivityContext);
  // The spin must not start while any other table animation is playing.
  const otherAnimationActive = dealerAnimating || chipsAnimating;

  // progress 1 = settled; <1 = queued/mid-rotation. settled = the POV currently
  // shown at rest; from/to = the active (or held) rotation's endpoints.
  const [progress, setProgress] = useState(1);
  const settledRef = useRef(effectivePov(pov));
  const fromRef = useRef(settledRef.current);
  const toRef = useRef(settledRef.current);
  const runningRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Live mirror of `progress` for `shouldHoldForRotation`, which must read a
  // stable callback (see its doc comment) rather than closing over state.
  const progressRef = useRef(progress);
  progressRef.current = progress;
  // When the last REAL spin (not a no-op snap) finished settling.
  const lastRotationEndRef = useRef(0);
  // RENDER-TIME (not effect-time) detection that the requested POV changed.
  // React commits a render parent-before-child but runs effects
  // child-before-parent, so a child mounting in the SAME commit as this POV
  // change (e.g. WinChips mounting because the winner = the new POV) runs its
  // effect BEFORE the effect below ever gets to flip `progress`— at that
  // instant `shouldHoldForRotation` reading only `progress`/`lastRotationEndRef`
  // sees "not rotating" and lets a replay through live (EN-10725). Comparing
  // during render instead makes the signal available to that child's effect
  // in the very same commit, regardless of whether a visible spin ever ends
  // up starting (the "too small to rotate" / background-resync snap branches
  // below are exactly where a replay-carrying broadcast is most likely).
  const lastSeenPovRef = useRef(pov);
  const lastPovChangeRef = useRef(0);
  if (lastSeenPovRef.current !== pov) {
    lastSeenPovRef.current = pov;
    lastPovChangeRef.current = Date.now();
  }
  // Live mirror of the gate so the deferred start (below) reads the current
  // value rather than the one captured when it was scheduled.
  const gateRef = useRef(otherAnimationActive);
  gateRef.current = otherAnimationActive;
  // Bumped when a rotation finishes so the effect re-evaluates (the POV may have
  // changed mid-spin, queuing the next rotation).
  const [completion, setCompletion] = useState(0);

  useEffect(() => {
    // Never disturb a spin already in flight — let it finish, then chain.
    if (runningRef.current) return;

    const want = effectivePov(pov);

    // Re-aim the (held) rotation at the latest requested POV from the settled
    // layout. Holding at progress 0 keeps the ring on its old positions instead
    // of snapping while we wait for a clear moment to spin.
    if (want !== toRef.current) {
      const resync = isTableResyncing(resyncGameID);
      // During a background resync (reconnect / idle re-subscribe), the fresh
      // snapshot burst can momentarily unmatch the player (seatId -1 → spectator
      // baseline) before the seated snapshot lands. Ignore that transient drop to
      // the spectator baseline so the ring never rotates away-and-back — the
      // player is already seated at this table and the resync must be silent.
      if (resync && want === effectivePov(-1) && settledRef.current !== effectivePov(-1)) {
        return;
      }
      fromRef.current = settledRef.current;
      toRef.current = want;
      // Snap (no entrance/rotation spin) when the arc would be a no-op, the table
      // is too small to rotate, OR a background resync is in progress — the player
      // is already viewing this table, so any real re-seat lands silently.
      if (want === settledRef.current || seatCount < 2 || resync) {
        settledRef.current = want;
        setProgress(1);
        return;
      }
      setProgress(0);
    }

    const hasPending = toRef.current !== settledRef.current && seatCount >= 2;
    if (!hasPending || otherAnimationActive) return;

    // Defer the actual start to a macrotask so any chip flights triggered by the
    // SAME update (e.g. WinChips remounting because the winner = the new POV)
    // have run their effects and registered. Only then, if the table is still
    // idle, spin. Until it fires the ring stays held at progress 0.
    if (startTimerRef.current !== null) return; // a start is already queued
    startTimerRef.current = setTimeout(() => {
      startTimerRef.current = null;
      // Re-check live: a concurrent animation may have registered, the POV may
      // have settled back, or a spin may already be running.
      if (runningRef.current || gateRef.current || seatCount < 2) return;
      if (toRef.current === settledRef.current) return;

      runningRef.current = true;
      const delta = mod(fromRef.current - toRef.current, seatCount);
      const durationMs = Math.min(1400, Math.max(450, delta * 130));
      let startTs: number | null = null;

      const tick = (now: number) => {
        if (startTs === null) startTs = now;
        const t = Math.min(1, (now - startTs) / durationMs);
        setProgress(easeInOutCubic(t));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          settledRef.current = toRef.current;
          runningRef.current = false;
          rafRef.current = null;
          lastRotationEndRef.current = Date.now();
          // Re-run the effect to pick up any POV change that arrived mid-spin.
          setCompletion((c) => c + 1);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    }, 0);
  }, [pov, otherAnimationActive, seatCount, completion]);

  // Cancel any in-flight frame / queued start on unmount only (re-runs above
  // must not abort a running spin).
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (startTimerRef.current !== null) clearTimeout(startTimerRef.current);
    },
    []
  );

  const slotFor = useCallback(
    (seatId: number) => {
      const from = fromRef.current;
      const to = toRef.current;
      // Start slot under the old POV → clockwise toward the new POV's slot.
      const startSlot = mod(seatId - from, seatCount);
      const delta = mod(from - to, seatCount);
      return startSlot + progress * delta;
    },
    [progress, seatCount]
  );

  const shouldHoldForRotation = useCallback(
    () =>
      progressRef.current < 1 ||
      Date.now() - lastRotationEndRef.current < ROTATION_SETTLE_GRACE_MS ||
      Date.now() - lastPovChangeRef.current < ROTATION_SETTLE_GRACE_MS,
    []
  );

  const value: SeatRotationContextType = {
    isRotating: progress < 1,
    layoutSeatId: pov,
    slotFor,
    seatAt: sampler.seatAt,
    betAt: sampler.betAt,
    shouldHoldForRotation
  };

  return <SeatRotationContext.Provider value={value}>{children}</SeatRotationContext.Provider>;
};
